/**
 * Pricing Utilities
 * Handles tax calculations, proportional vendor shipping allocations, markup calculations, and Client Price computation.
 */

/**
 * Retrieves the global default tax rate percentage from localStorage.
 * @returns {number} Default tax percentage (e.g. 7.5 or 0)
 */
export function getDefaultTaxRate() {
  try {
    const saved = localStorage.getItem("global_default_tax_rate");
    if (saved !== null && saved !== "") {
      const parsed = parseFloat(saved);
      return !isNaN(parsed) && parsed >= 0 ? parsed : 0;
    }
  } catch (e) {}
  return 0;
}

/**
 * Resolves the effective tax percentage for a line item or vendor split.
 * If isTaxable is explicitly false or tax is false/"false", tax rate is 0%.
 * Otherwise (default checked), uses defaultRate or global default tax rate.
 *
 * @param {Object} v - Line item or vendor split object
 * @param {number|null} defaultRate - Optional override for default tax rate
 * @returns {number} Effective tax percentage
 */
export function resolveItemTaxRate(v, defaultRate = null) {
  if (!v) return 0;
  if (v.isTaxable === false || v.tax === false || v.tax === "false") {
    return 0;
  }
  const globalDefault = defaultRate !== null && defaultRate !== undefined
    ? Number(defaultRate)
    : getDefaultTaxRate();
  return Math.max(0, globalDefault);
}

/**
 * Safely extracts the Shipping Cost Out from an estimate/PO object.
 * Checks shippingCostOut first, then estShippingCost fallback.
 * @param {Object} po - Estimate or PO object
 * @returns {number} Shipping Cost Out amount
 */
export function getPoShippingCostOut(po) {
  if (!po) return 0;
  if (po.shippingCostOut !== undefined && po.shippingCostOut !== null && po.shippingCostOut !== "") {
    const val = Number(po.shippingCostOut);
    return !isNaN(val) && val >= 0 ? val : 0;
  }
  if (po.estShippingCost !== undefined && po.estShippingCost !== null && po.estShippingCost !== "") {
    const val = Number(po.estShippingCost);
    return !isNaN(val) && val >= 0 ? val : 0;
  }
  return 0;
}

/**
 * Calculates shipping multipliers per unique vendor across line items in a PO.
 * Formula:
 *   1. Vendor Subtotal = Sum of (Unit price * QTY) for each vendor
 *   2. Vendor Shipping Cost / Vendor Subtotal = Decimal Shipping (allow for 6 decimal units)
 *
 * @param {Array} items - Array of PO line items
 * @param {number|null} defaultTaxRate - Optional default tax rate (kept for signature compatibility)
 * @returns {Object} Map of lowercase trimmed vendor name to { vendorSubtotal, totalShipping, decimalShipping, multiplier }
 */
export function calculateVendorShippingMultipliers(items, defaultTaxRate = null) {
  if (!items || items.length === 0) return {};
  const vendorTotals = {};

  items.forEach(item => {
    const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
      {
        vendor: item.vendor || "",
        quantity: item.quantity,
        vendorPrice: item.vendorPrice,
        tax: item.tax,
        isTaxable: item.isTaxable,
        shippingCost: item.shippingCost
      }
    ];

    itemVendors.forEach(v => {
      const vKey = (v.vendor || "").trim().toLowerCase();
      if (!vKey) return;

      if (!vendorTotals[vKey]) {
        vendorTotals[vKey] = { totalShipping: 0, vendorSubtotal: 0 };
      }
      vendorTotals[vKey].totalShipping += Number(v.shippingCost || 0);

      // Step 1: Vendor Subtotal = (Unit price * QTY)
      const baseSubtotal = Number(v.quantity || 0) * Number(v.vendorPrice || 0);
      vendorTotals[vKey].vendorSubtotal += baseSubtotal;
    });
  });

  const multipliers = {};
  Object.keys(vendorTotals).forEach(vKey => {
    const { totalShipping, vendorSubtotal } = vendorTotals[vKey];
    // Step 2: Vendor Shipping Cost / Vendor Subtotal = Decimal Shipping (allow for 6 decimal units)
    const decimalShipping = vendorSubtotal > 0 ? Number((totalShipping / vendorSubtotal).toFixed(6)) : 0;
    multipliers[vKey] = {
      vendorSubtotal,
      totalShipping,
      decimalShipping,
      multiplier: 1 + decimalShipping
    };
  });

  return multipliers;
}

/**
 * Calculates the base Total Estimate Amount across items (prior to applying outbound shipping factor).
 * Formula: Sum across items of [Line Total Cost * 1.MU%] (or + markupDollar if custom dollar).
 *
 * @param {Array} items - Line items in the PO
 * @param {number|Function} getEffectiveMarkup - Number or function(item) returning markup percentage
 * @param {Object} vendorMultipliers - Precalculated vendor multipliers map
 * @param {number|null} defaultTaxRate - Optional default tax rate
 * @returns {number} Base total estimate amount
 */
export function calculateBaseTotalEstimateAmount(items, getEffectiveMarkup = 0, vendorMultipliers = null, defaultTaxRate = null) {
  if (!items || items.length === 0) return 0;
  const multipliers = vendorMultipliers || calculateVendorShippingMultipliers(items, defaultTaxRate);
  return items.reduce((acc, item) => {
    const rate = typeof getEffectiveMarkup === "function" ? getEffectiveMarkup(item) : getEffectiveMarkup;
    const lineTotalCost = calculateLineTotalCost(item, multipliers, defaultTaxRate);
    let baseClientPrice;
    if (item && item.isMarkupCustom && item.markupType === "dollar" && item.markupDollar !== undefined && item.markupDollar !== null) {
      baseClientPrice = lineTotalCost + Number(item.markupDollar);
    } else {
      const itemRate = (item && item.isMarkupCustom && item.markupPercentage !== undefined && item.markupPercentage !== null)
        ? Number(item.markupPercentage)
        : Number(rate || 0);
      baseClientPrice = lineTotalCost * (1 + itemRate / 100);
    }
    return acc + (isNaN(baseClientPrice) ? 0 : baseClientPrice);
  }, 0);
}

/**
 * Calculates New Decimal (outbound shipping factor).
 * Formula:
 *   Step 5. Shipping Out / Total Cost Amount = New Decimal (allow for 6 decimals units)
 *
 * @param {number} shippingOut - Shipping out amount input by user in Shipping Cost Out box
 * @param {number} totalCostAmount - Total material cost amount (sum of Step 4 Total Cost across items, without markup)
 * @returns {number} Decimal factor with up to 6 decimal precision
 */
export function calculateNewDecimal(shippingOut, totalCostAmount) {
  const sOut = Number(shippingOut || 0);
  const costAmt = Number(totalCostAmount || 0);
  if (sOut <= 0 || costAmt <= 0) return 0;
  return Number((sOut / costAmt).toFixed(6));
}

/**
 * Recalculates Client Price for a single line item using the 6-step calculation sequence:
 *   Step 1: Vendor Subtotal = (Unit price * QTY)
 *   Step 2: Vendor Shipping Cost / Vendor Subtotal = Decimal Shipping (allow for 6 decimals units)
 *   Step 3: (Unit Price * 1.Decimal Shipping) * 1.tax rate = New Unit Price
 *   Step 4: New Unit Price * QTY = Total Cost
 *   Step 5: Shipping Out / Total Cost Amount = New Decimal (allow for 6 decimals units)
 *   Step 6: Client Price = ((New Unit Price * 1.New Decimal) * 1.MU %) * QTY
 *
 * @param {Object} item - The line item
 * @param {number} effectiveMarkupRate - Effective markup rate percentage (including CC fee if applicable)
 * @param {Object} vendorMultipliers - Precomputed multipliers map from calculateVendorShippingMultipliers
 * @param {number|null} defaultTaxRate - Optional default tax rate
 * @param {number} newDecimal - Outbound shipping factor from Step 5
 * @returns {number} Rounded client price (2 decimal places)
 */
export function recalculateItemClientPrice(item, effectiveMarkupRate = 0, vendorMultipliers = {}, defaultTaxRate = null, newDecimal = 0) {
  if (!item) return 0;
  const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
    {
      vendor: item.vendor || "",
      quantity: item.quantity,
      vendorPrice: item.vendorPrice,
      tax: item.tax,
      isTaxable: item.isTaxable,
      shippingCost: item.shippingCost
    }
  ];

  const shippingOutMult = 1 + Number(newDecimal || 0);
  const isDollarMarkup = Boolean(item && item.isMarkupCustom && item.markupType === "dollar" && item.markupDollar !== undefined && item.markupDollar !== null);
  const itemRate = (item && item.isMarkupCustom && item.markupPercentage !== undefined && item.markupPercentage !== null)
    ? Number(item.markupPercentage)
    : Number(effectiveMarkupRate || 0);

  let totalLandedCost = 0;
  let totalClientPrice = 0;

  itemVendors.forEach(v => {
    const qty = Number(v.quantity || 0);
    const unitPrice = Number(v.vendorPrice || 0);
    const shipping = Number(v.shippingCost || 0);
    const taxRate = resolveItemTaxRate(v, defaultTaxRate);
    const vKey = (v.vendor || "").trim().toLowerCase();
    const vData = vKey && vendorMultipliers ? vendorMultipliers[vKey] : null;

    let decimalShipping = 0;
    if (vData && typeof vData === "object" && vData.decimalShipping !== undefined) {
      decimalShipping = Number(vData.decimalShipping || 0);
    } else {
      const splitSubtotal = unitPrice * qty;
      decimalShipping = splitSubtotal > 0 ? Number((shipping / splitSubtotal).toFixed(6)) : 0;
    }

    // Step 3: (Unit Price * 1.Decimal Shipping) * 1.tax rate = New Unit Price
    const newUnitPrice = (unitPrice * (1 + decimalShipping)) * (1 + taxRate / 100);

    // Landed unit price with outbound shipping
    const landedUnitPrice = newUnitPrice * shippingOutMult;

    if (unitPrice === 0 && (unitPrice * qty) === 0 && shipping > 0) {
      const standaloneShipping = shipping * (1 + taxRate / 100) * shippingOutMult;
      totalLandedCost += standaloneShipping;
      totalClientPrice += standaloneShipping * (1 + itemRate / 100);
    } else {
      totalLandedCost += landedUnitPrice * qty;
      // Step 6: ((New Unit Price * 1.New Decimal) * 1.MU %) * QTY
      totalClientPrice += ((landedUnitPrice * (1 + itemRate / 100))) * qty;
    }
  });

  const finalClientPrice = isDollarMarkup
    ? totalLandedCost + Number(item.markupDollar)
    : totalClientPrice;

  return Number(finalClientPrice.toFixed(2));
}

/**
 * Recalculates client prices for all line items in a PO.
 *
 * @param {Array} items - Line items in the PO
 * @param {number|Function} getEffectiveMarkup - Number or function(item) returning markup percentage
 * @param {number|null} defaultTaxRate - Optional default tax rate
 * @param {number} shippingOut - Shipping out amount input by user
 * @returns {Array} Updated items with recalculated unitPrice
 */
export function recalculateAllPoItemClientPrices(items, getEffectiveMarkup = 0, defaultTaxRate = null, shippingOut = 0) {
  if (!items || items.length === 0) return [];
  const multipliers = calculateVendorShippingMultipliers(items, defaultTaxRate);
  const totalCost = calculateTotalCostSum(items, defaultTaxRate, multipliers);
  const newDecimal = calculateNewDecimal(shippingOut, totalCost);

  return items.map(item => {
    const rate = typeof getEffectiveMarkup === "function" ? getEffectiveMarkup(item) : getEffectiveMarkup;
    return {
      ...item,
      unitPrice: recalculateItemClientPrice(item, rate, multipliers, defaultTaxRate, newDecimal)
    };
  });
}

/**
 * Calculates the total line cost for an item following the 5-step pricing calculation:
 *   Step 1: Vendor Subtotal = (Unit price * QTY)
 *   Step 2: Vendor Shipping Cost / Vendor Subtotal = Decimal Shipping (allow for 6 decimals)
 *   Step 3: (Unit Price * 1.Decimal Shipping) * 1.tax rate = New Unit Price
 *   Step 4: New Unit Price * QTY = Total Cost
 *
 * @param {Object} item - PO line item
 * @param {Object|number|null} vendorMultipliersOrTax - Vendor multipliers map OR default tax rate override
 * @param {number|null} defaultTaxRate - Optional default tax rate if first param is multipliers
 * @returns {number} Line total cost (sum across vendor splits)
 */
export function calculateLineTotalCost(item, vendorMultipliersOrTax = null, defaultTaxRate = null) {
  if (!item) return 0;

  let vendorMultipliers = null;
  let taxOverride = defaultTaxRate;
  if (vendorMultipliersOrTax !== null && typeof vendorMultipliersOrTax === "object") {
    vendorMultipliers = vendorMultipliersOrTax;
  } else if (vendorMultipliersOrTax !== null && typeof vendorMultipliersOrTax !== "object") {
    taxOverride = vendorMultipliersOrTax;
  }

  const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
    {
      vendor: item.vendor || "",
      quantity: item.quantity,
      vendorPrice: item.vendorPrice,
      tax: item.tax,
      isTaxable: item.isTaxable,
      shippingCost: item.shippingCost
    }
  ];

  return itemVendors.reduce((sum, v) => {
    const qty = Number(v.quantity || 0);
    const unitPrice = Number(v.vendorPrice || 0);
    const shipping = Number(v.shippingCost || 0);
    const taxRate = resolveItemTaxRate(v, taxOverride);
    const vKey = (v.vendor || "").trim().toLowerCase();
    const vData = vKey && vendorMultipliers ? vendorMultipliers[vKey] : null;

    let decimalShipping = 0;
    if (vData && typeof vData === "object" && vData.decimalShipping !== undefined) {
      decimalShipping = Number(vData.decimalShipping || 0);
    } else {
      const splitSubtotal = unitPrice * qty;
      decimalShipping = splitSubtotal > 0 ? Number((shipping / splitSubtotal).toFixed(6)) : 0;
    }

    // Step 3: (Unit Price * 1.Decimal Shipping) * 1.tax rate = New Unit Price
    const newUnitPrice = (unitPrice * (1 + decimalShipping)) * (1 + taxRate / 100);

    // Step 4: New Unit Price * QTY = Total Cost
    let splitTotalCost = 0;
    if (unitPrice === 0 && (unitPrice * qty) === 0 && shipping > 0) {
      splitTotalCost = shipping * (1 + taxRate / 100);
    } else {
      splitTotalCost = newUnitPrice * qty;
    }

    return sum + splitTotalCost;
  }, 0);
}

/**
 * Calculates the total cost sum across all line items in a PO (including tax and shipping).
 *
 * @param {Array} items - PO line items
 * @param {number|null} defaultTaxRate - Optional default tax rate
 * @param {Object|null} vendorMultipliers - Optional precalculated vendor multipliers
 * @returns {number} Total PO cost
 */
export function calculateTotalCostSum(items, defaultTaxRate = null, vendorMultipliers = null) {
  if (!items || items.length === 0) return 0;
  const multipliers = vendorMultipliers || calculateVendorShippingMultipliers(items, defaultTaxRate);
  return items.reduce((sum, item) => sum + calculateLineTotalCost(item, multipliers, defaultTaxRate), 0);
}

/**
 * Calculates the total sum of vendor shipping costs across all line items in a PO.
 *
 * @param {Array} items - PO line items
 * @returns {number} Total shipping cost
 */
export function calculateTotalShippingSum(items) {
  if (!items || items.length === 0) return 0;
  return items.reduce((sum, item) => {
    const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
      { shippingCost: item.shippingCost }
    ];
    const lineShipping = itemVendors.reduce((vSum, v) => vSum + Number(v.shippingCost || 0), 0);
    return sum + lineShipping;
  }, 0);
}
